<script>
  import { onMount } from "svelte";
  import {
    Page,
    Row,
    Col,
    Button,
    Card,
    CardContent,
    Preloader,
    f7,
    f7ready,
  } from "framework7-svelte";

  import AdminNavbar from "../components/common/AdminNavbar.svelte";
  import KpiStatCard from "../components/common/KpiStatCard.svelte";
  import StatusBadge from "../components/common/StatusBadge.svelte";
  import CsvExportButton from "../components/common/CsvExportButton.svelte";

  import { adminQuery } from "../services/adminGQL";
  import { formatINR, formatNumber } from "../utils/formatters";
  import { downloadCsv } from "../utils/csvExporter";

  export let f7route;
  export let f7router;

  let activeReport = "portfolio"; // 'portfolio' | 'net_position' | 'longshort' | 'ledger'
  let targetUcc = "";
  let searchQuery = "";
  let loading = false;

  // Stored fetched report datasets
  let reportData = {
    portfolio: [],
    net_position: [],
    longshort: [],
    ledger: [],
  };

  onMount(() => {
    f7ready(() => {
      targetUcc = (f7route.query && f7route.query.ucc) ? String(f7route.query.ucc).trim() : "";
      if (targetUcc) {
        searchQuery = targetUcc;
      }
      fetchReport(activeReport);
    });
  });

  async function fetchReport(reportName) {
    loading = true;
    try {
      const res = await adminQuery(
        "fetch_all_reports",
        { report_name: reportName },
        `
          portfolio { ucc portfolio_value fund_invested ledger_balance portfolio_data { scrip_name holding_qty rate_per_share total_value } }
          net_position { client_code netposition_pnl transaction_charges { tran_charges sebi_charges stt stamp_duty_charges central_gst state_gst } }
          longshort { user_id ucc buy_total sell_total longshort_value previous_longshort_value modified_ts long_short_data { scrip_name buy_qty buy_value buy_avg sell_qty sell_value sell_avg } }
          ledger { ucc ledger_balance net_fund type }
          success
          message
        `
      );

      const payload = res?.data?.fetch_all_reports || {};
      if (payload[reportName]) {
        reportData[reportName] = payload[reportName];
      }
    } catch (err) {
      console.error(`Fetch report ${reportName} error:`, err);
      f7.dialog.alert(err.message || `Failed to fetch ${reportName} report`, "Report Error");
    } finally {
      loading = false;
    }
  }

  function handleTabChange(tabName) {
    if (activeReport === tabName) return;
    activeReport = tabName;
    if (!reportData[tabName] || reportData[tabName].length === 0) {
      fetchReport(tabName);
    }
  }

  // --- NORMALIZATION ENGINES ---

  // 1. Flattened Portfolio Scrips
  $: flattenedPortfolio = (reportData.portfolio || []).flatMap((userPort) => {
    const ucc = userPort.ucc || "-";
    const portVal = userPort.portfolio_value;
    const ledger = userPort.ledger_balance;
    const scrips = userPort.portfolio_data || [];

    if (scrips.length === 0) {
      return [{
        ucc,
        portfolio_value: portVal,
        ledger_balance: ledger,
        scrip_name: "No holdings",
        holding_qty: 0,
        rate_per_share: 0,
        total_value: 0,
      }];
    }

    return scrips.map((s) => ({
      ucc,
      portfolio_value: portVal,
      ledger_balance: ledger,
      scrip_name: s.scrip_name || "-",
      holding_qty: s.holding_qty || 0,
      rate_per_share: s.rate_per_share || 0,
      total_value: s.total_value || 0,
    }));
  });

  // 2. Flattened Net Position & Transaction Charges
  $: flattenedNetPosition = (reportData.net_position || []).map((np) => {
    const ch = np.transaction_charges || {};
    return {
      client_code: np.client_code || "-",
      netposition_pnl: np.netposition_pnl || 0,
      tran_charges: ch.tran_charges || 0,
      sebi_charges: ch.sebi_charges || 0,
      stt: ch.stt || 0,
      stamp_duty: ch.stamp_duty_charges || 0,
      gst: (parseFloat(ch.central_gst) || 0) + (parseFloat(ch.state_gst) || 0),
      total_charges:
        (parseFloat(ch.tran_charges) || 0) +
        (parseFloat(ch.sebi_charges) || 0) +
        (parseFloat(ch.stt) || 0) +
        (parseFloat(ch.stamp_duty_charges) || 0) +
        (parseFloat(ch.central_gst) || 0) +
        (parseFloat(ch.state_gst) || 0),
    };
  });

  // 3. Flattened Long/Short Scrips
  $: flattenedLongShort = (reportData.longshort || []).flatMap((ls) => {
    const ucc = ls.ucc || "-";
    const lsVal = ls.longshort_value || 0;
    const buyTot = ls.buy_total || 0;
    const sellTot = ls.sell_total || 0;
    const items = ls.long_short_data || [];

    if (items.length === 0) {
      return [{
        ucc,
        longshort_value: lsVal,
        buy_total: buyTot,
        sell_total: sellTot,
        scrip_name: "No trades today",
        buy_qty: 0,
        buy_avg: 0,
        buy_value: 0,
        sell_qty: 0,
        sell_avg: 0,
        sell_value: 0,
      }];
    }

    return items.map((it) => ({
      ucc,
      longshort_value: lsVal,
      buy_total: buyTot,
      sell_total: sellTot,
      scrip_name: it.scrip_name || "-",
      buy_qty: it.buy_qty || 0,
      buy_avg: it.buy_avg || 0,
      buy_value: it.buy_value || 0,
      sell_qty: it.sell_qty || 0,
      sell_avg: it.sell_avg || 0,
      sell_value: it.sell_value || 0,
    }));
  });

  // 4. Ledger Normalizer
  $: flattenedLedger = (reportData.ledger || []).map((l) => ({
    ucc: l.ucc || "-",
    ledger_balance: l.ledger_balance || 0,
    net_fund: l.net_fund || 0,
    type: l.type || "-",
  }));

  // Filtering by search/UCC query
  function filterRows(rows, fields) {
    if (!searchQuery.trim()) return rows;
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((r) =>
      fields.some((f) => String(r[f] || "").toLowerCase().includes(q))
    );
  }

  $: displayedPortfolio = filterRows(flattenedPortfolio, ["ucc", "scrip_name"]);
  $: displayedNetPosition = filterRows(flattenedNetPosition, ["client_code"]);
  $: displayedLongShort = filterRows(flattenedLongShort, ["ucc", "scrip_name"]);
  $: displayedLedger = filterRows(flattenedLedger, ["ucc", "type"]);

  // Dynamic KPI Stats
  $: portfolioTotalHoldingValue = displayedPortfolio.reduce((acc, r) => acc + (parseFloat(r.total_value) || 0), 0);
  $: netPositionTotalCharges = displayedNetPosition.reduce((acc, r) => acc + (parseFloat(r.total_charges) || 0), 0);
  $: longShortTotalBuy = displayedLongShort.reduce((acc, r) => acc + (parseFloat(r.buy_value) || 0), 0);
  $: longShortTotalSell = displayedLongShort.reduce((acc, r) => acc + (parseFloat(r.sell_value) || 0), 0);
  $: ledgerAggregateBalance = displayedLedger.reduce((acc, r) => acc + (parseFloat(r.ledger_balance) || 0), 0);

  // Column definitions for CSV exports
  const portfolioCols = [
    { header: "UCC", key: "ucc" },
    { header: "Scrip Name", key: "scrip_name" },
    { header: "Holding Qty", key: "holding_qty" },
    { header: "Rate / Share (INR)", key: "rate_per_share" },
    { header: "Total Value (INR)", key: "total_value" },
    { header: "Ledger Balance", key: "ledger_balance" },
    { header: "Portfolio Total", key: "portfolio_value" },
  ];

  const netPosCols = [
    { header: "Client Code (UCC)", key: "client_code" },
    { header: "Net Position PnL", key: "netposition_pnl" },
    { header: "Transaction Charges", key: "tran_charges" },
    { header: "SEBI Charges", key: "sebi_charges" },
    { header: "STT", key: "stt" },
    { header: "Stamp Duty", key: "stamp_duty" },
    { header: "GST Total", key: "gst" },
    { header: "Total Charges (INR)", key: "total_charges" },
  ];

  const longShortCols = [
    { header: "UCC", key: "ucc" },
    { header: "Scrip Name", key: "scrip_name" },
    { header: "Buy Qty", key: "buy_qty" },
    { header: "Buy Avg (INR)", key: "buy_avg" },
    { header: "Buy Value (INR)", key: "buy_value" },
    { header: "Sell Qty", key: "sell_qty" },
    { header: "Sell Avg (INR)", key: "sell_avg" },
    { header: "Sell Value (INR)", key: "sell_value" },
    { header: "Long/Short Total Value", key: "longshort_value" },
  ];

  const ledgerCols = [
    { header: "UCC", key: "ucc" },
    { header: "Ledger Balance (INR)", key: "ledger_balance" },
    { header: "Net Fund (INR)", key: "net_fund" },
    { header: "Balance Type", key: "type" },
  ];

  // Batch Export all 4 reports
  async function exportAllReportsBatch() {
    f7.toast.create({ text: "Preparing batch export of all 4 operational reports...", closeTimeout: 2000 }).open();
    // Ensure all 4 are fetched
    const needed = ["portfolio", "net_position", "longshort", "ledger"].filter(
      (n) => !reportData[n] || reportData[n].length === 0
    );

    for (const n of needed) {
      await fetchReport(n);
    }

    const dateStr = new Date().toISOString().slice(0, 10);
    downloadCsv(`portfolio_report_${dateStr}.csv`, portfolioCols, flattenedPortfolio);
    setTimeout(() => downloadCsv(`net_position_charges_${dateStr}.csv`, netPosCols, flattenedNetPosition), 500);
    setTimeout(() => downloadCsv(`long_short_positions_${dateStr}.csv`, longShortCols, flattenedLongShort), 1000);
    setTimeout(() => downloadCsv(`ledger_balances_${dateStr}.csv`, ledgerCols, flattenedLedger), 1500);

    f7.toast.create({ text: "✓ Batch CSVs downloaded successfully!", closeTimeout: 3000 }).open();
  }
</script>

<Page class="all-reports-hub-page">
  <AdminNavbar
    title="Operational Reports Hub"
    subtitle="Task 6: Portfolio, Net Positions, Long/Short & Ledger Normalizer"
    backLink={true}
    backLinkUrl="/all-users-home-data/"
  />

  <div class="page-content-wrapper padding">
    <!-- Context Indicator when filtering by specific UCC -->
    {#if targetUcc}
      <div class="ucc-context-banner margin-bottom">
        <span>Displaying reports filtered for member UCC: <strong>{targetUcc}</strong></span>
        <button class="clear-ucc-btn" on:click={() => { targetUcc = ""; searchQuery = ""; }}>
          Show All Members
        </button>
      </div>
    {/if}

    <!-- Dynamic KPI Header -->
    <Row class="margin-bottom">
      {#if activeReport === "portfolio"}
        <Col width="100" medium="33">
          <KpiStatCard
            title="Total Holding Value"
            value={formatINR(portfolioTotalHoldingValue)}
            subtitle="Across {displayedPortfolio.length} holding scrips"
            icon="f7:chart_pie_fill"
            iconColor="#2563eb"
            theme="info"
          />
        </Col>
        <Col width="100" medium="33">
          <KpiStatCard
            title="Audited Portfolios"
            value={reportData.portfolio.length}
            subtitle="Client Accounts"
            icon="f7:person_2_fill"
            iconColor="#475569"
            theme="default"
          />
        </Col>
        <Col width="100" medium="33">
          <KpiStatCard
            title="Total Holding Scrips"
            value={displayedPortfolio.length}
            subtitle="Normalized Equity Rows"
            icon="f7:square_stack_3d_up_fill"
            iconColor="#10b981"
            theme="success"
          />
        </Col>
      {:else if activeReport === "net_position"}
        <Col width="100" medium="33">
          <KpiStatCard
            title="Total Transaction Fees"
            value={formatINR(netPositionTotalCharges)}
            subtitle="STT, SEBI, GST & Brokerage"
            icon="f7:money_dollar_circle_fill"
            iconColor="#ef4444"
            theme="danger"
          />
        </Col>
        <Col width="100" medium="33">
          <KpiStatCard
            title="Client Net Positions"
            value={displayedNetPosition.length}
            subtitle="Position statements"
            icon="f7:doc_plaintext"
            iconColor="#2563eb"
            theme="default"
          />
        </Col>
        <Col width="100" medium="33">
          <KpiStatCard
            title="Reconciliation Status"
            value="OK"
            subtitle="All fee lines populated"
            icon="f7:checkmark_shield_fill"
            iconColor="#10b981"
            theme="success"
          />
        </Col>
      {:else if activeReport === "longshort"}
        <Col width="100" medium="33">
          <KpiStatCard
            title="Total Buy Volume"
            value={formatINR(longShortTotalBuy)}
            subtitle="Day Purchases"
            icon="f7:arrow_up_circle_fill"
            iconColor="#10b981"
            theme="success"
          />
        </Col>
        <Col width="100" medium="33">
          <KpiStatCard
            title="Total Sell Volume"
            value={formatINR(longShortTotalSell)}
            subtitle="Day Sales"
            icon="f7:arrow_down_circle_fill"
            iconColor="#ef4444"
            theme="danger"
          />
        </Col>
        <Col width="100" medium="33">
          <KpiStatCard
            title="Active Scrips"
            value={displayedLongShort.length}
            subtitle="Intraday positions"
            icon="f7:waveform_path"
            iconColor="#8b5cf6"
            theme="purple"
          />
        </Col>
      {:else if activeReport === "ledger"}
        <Col width="100" medium="33">
          <KpiStatCard
            title="Aggregate Ledger"
            value={formatINR(ledgerAggregateBalance)}
            subtitle="Net Client Fund Position"
            icon="f7:book_fill"
            iconColor="#2563eb"
            theme="info"
          />
        </Col>
        <Col width="100" medium="33">
          <KpiStatCard
            title="Audited Ledgers"
            value={displayedLedger.length}
            subtitle="Accounts with entries"
            icon="f7:person_2_fill"
            iconColor="#475569"
            theme="default"
          />
        </Col>
        <Col width="100" medium="33">
          <KpiStatCard
            title="Balance Health"
            value="Active"
            subtitle="Ledger data synchronized"
            icon="f7:checkmark_circle_fill"
            iconColor="#10b981"
            theme="success"
          />
        </Col>
      {/if}
    </Row>

    <!-- Toolbar & Tab Navigation -->
    <div class="reports-toolbar margin-bottom">
      <div class="tabs-segment">
        <div class="segmented segmented-raised">
          <Button
            active={activeReport === "portfolio"}
            on:click={() => handleTabChange("portfolio")}
            class="rep-tab"
          >
            <span>📊 Portfolio</span>
          </Button>
          <Button
            active={activeReport === "net_position"}
            on:click={() => handleTabChange("net_position")}
            class="rep-tab"
          >
            <span>📑 Net Position</span>
          </Button>
          <Button
            active={activeReport === "longshort"}
            on:click={() => handleTabChange("longshort")}
            class="rep-tab"
          >
            <span>⚖️ Long / Short</span>
          </Button>
          <Button
            active={activeReport === "ledger"}
            on:click={() => handleTabChange("ledger")}
            class="rep-tab"
          >
            <span>📒 Ledger</span>
          </Button>
        </div>
      </div>

      <!-- Search Box -->
      <div class="search-input-wrap">
        <input
          type="text"
          placeholder="Filter by UCC or Scrip name..."
          bind:value={searchQuery}
          class="search-input"
        />
        {#if searchQuery}
          <button class="clear-search" on:click={() => (searchQuery = "")}>✕</button>
        {/if}
      </div>

      <!-- Action Buttons -->
      <div class="actions-group">
        {#if activeReport === "portfolio"}
          <CsvExportButton
            filename="portfolio_report_{new Date().toISOString().slice(0, 10)}.csv"
            columns={portfolioCols}
            data={displayedPortfolio}
            label="Export Portfolio CSV"
          />
        {:else if activeReport === "net_position"}
          <CsvExportButton
            filename="net_position_charges_{new Date().toISOString().slice(0, 10)}.csv"
            columns={netPosCols}
            data={displayedNetPosition}
            label="Export Charges CSV"
          />
        {:else if activeReport === "longshort"}
          <CsvExportButton
            filename="long_short_report_{new Date().toISOString().slice(0, 10)}.csv"
            columns={longShortCols}
            data={displayedLongShort}
            label="Export Long/Short CSV"
          />
        {:else if activeReport === "ledger"}
          <CsvExportButton
            filename="ledger_balances_{new Date().toISOString().slice(0, 10)}.csv"
            columns={ledgerCols}
            data={displayedLedger}
            label="Export Ledger CSV"
          />
        {/if}

        <Button outline small color="purple" on:click={exportAllReportsBatch}>
          <span>📦 Batch Export All</span>
        </Button>

        <Button outline small color="blue" on:click={() => fetchReport(activeReport)} disabled={loading}>
          {#if loading}
            <Preloader size={14} />
          {:else}
            <span>🔄 Refresh</span>
          {/if}
        </Button>
      </div>
    </div>

    <!-- Data Table Card -->
    <Card class="table-card">
      <CardContent class="no-padding">
        {#if loading}
          <div class="loading-state">
            <Preloader size={32} />
            <p>Fetching and normalizing {activeReport} data via GraphQL...</p>
          </div>
        {:else}
          <!-- Tab 1: Portfolio Holdings -->
          {#if activeReport === "portfolio"}
            {#if displayedPortfolio.length === 0}
              <div class="empty-state"><p>No portfolio holdings found.</p></div>
            {:else}
              <div class="data-table-responsive">
                <table class="rep-table">
                  <thead>
                    <tr>
                      <th style="width: 40px;">#</th>
                      <th>UCC</th>
                      <th>Scrip Name</th>
                      <th>Holding Qty</th>
                      <th>Rate / Share</th>
                      <th>Total Value</th>
                      <th>Ledger Balance</th>
                      <th>Account Portfolio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {#each displayedPortfolio as row, i}
                      <tr>
                        <td class="col-index">{i + 1}</td>
                        <td class="col-ucc">{row.ucc}</td>
                        <td class="col-scrip"><strong>{row.scrip_name}</strong></td>
                        <td class="col-qty">{row.holding_qty}</td>
                        <td class="col-amount">{formatINR(row.rate_per_share)}</td>
                        <td class="col-amount"><strong>{formatINR(row.total_value)}</strong></td>
                        <td class="col-amount">{formatINR(row.ledger_balance)}</td>
                        <td class="col-amount">{formatINR(row.portfolio_value)}</td>
                      </tr>
                    {/each}
                  </tbody>
                </table>
              </div>
            {/if}

          <!-- Tab 2: Net Position Charges -->
          {:else if activeReport === "net_position"}
            {#if displayedNetPosition.length === 0}
              <div class="empty-state"><p>No net position records found.</p></div>
            {:else}
              <div class="data-table-responsive">
                <table class="rep-table">
                  <thead>
                    <tr>
                      <th style="width: 40px;">#</th>
                      <th>Client Code</th>
                      <th>PnL</th>
                      <th>Tran Charges</th>
                      <th>SEBI Charges</th>
                      <th>STT</th>
                      <th>Stamp Duty</th>
                      <th>GST (CGST+SGST)</th>
                      <th>Total Fees</th>
                    </tr>
                  </thead>
                  <tbody>
                    {#each displayedNetPosition as row, i}
                      <tr>
                        <td class="col-index">{i + 1}</td>
                        <td class="col-ucc">{row.client_code}</td>
                        <td class="col-amount" class:text-green={parseFloat(row.netposition_pnl) >= 0} class:text-red={parseFloat(row.netposition_pnl) < 0}>
                          {formatINR(row.netposition_pnl)}
                        </td>
                        <td class="col-amount">{formatINR(row.tran_charges)}</td>
                        <td class="col-amount">{formatINR(row.sebi_charges)}</td>
                        <td class="col-amount">{formatINR(row.stt)}</td>
                        <td class="col-amount">{formatINR(row.stamp_duty)}</td>
                        <td class="col-amount">{formatINR(row.gst)}</td>
                        <td class="col-amount"><strong>{formatINR(row.total_charges)}</strong></td>
                      </tr>
                    {/each}
                  </tbody>
                </table>
              </div>
            {/if}

          <!-- Tab 3: Long / Short Positions -->
          {:else if activeReport === "longshort"}
            {#if displayedLongShort.length === 0}
              <div class="empty-state"><p>No long/short trades recorded today.</p></div>
            {:else}
              <div class="data-table-responsive">
                <table class="rep-table">
                  <thead>
                    <tr>
                      <th style="width: 40px;">#</th>
                      <th>UCC</th>
                      <th>Scrip Name</th>
                      <th>Buy Qty</th>
                      <th>Buy Avg</th>
                      <th>Buy Value</th>
                      <th>Sell Qty</th>
                      <th>Sell Avg</th>
                      <th>Sell Value</th>
                      <th>Net L/S Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {#each displayedLongShort as row, i}
                      <tr>
                        <td class="col-index">{i + 1}</td>
                        <td class="col-ucc">{row.ucc}</td>
                        <td class="col-scrip"><strong>{row.scrip_name}</strong></td>
                        <td class="col-qty">{row.buy_qty}</td>
                        <td class="col-amount">{formatINR(row.buy_avg)}</td>
                        <td class="col-amount">{formatINR(row.buy_value)}</td>
                        <td class="col-qty">{row.sell_qty}</td>
                        <td class="col-amount">{formatINR(row.sell_avg)}</td>
                        <td class="col-amount">{formatINR(row.sell_value)}</td>
                        <td class="col-amount"><strong>{formatINR(row.longshort_value)}</strong></td>
                      </tr>
                    {/each}
                  </tbody>
                </table>
              </div>
            {/if}

          <!-- Tab 4: Ledger Balances -->
          {:else if activeReport === "ledger"}
            {#if displayedLedger.length === 0}
              <div class="empty-state"><p>No ledger entries recorded.</p></div>
            {:else}
              <div class="data-table-responsive">
                <table class="rep-table">
                  <thead>
                    <tr>
                      <th style="width: 40px;">#</th>
                      <th>UCC</th>
                      <th>Ledger Balance</th>
                      <th>Net Fund</th>
                      <th>Balance Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {#each displayedLedger as row, i}
                      <tr>
                        <td class="col-index">{i + 1}</td>
                        <td class="col-ucc">{row.ucc}</td>
                        <td class="col-amount">
                          <strong class:text-red={parseFloat(row.ledger_balance) < 0}>
                            {formatINR(row.ledger_balance)}
                          </strong>
                        </td>
                        <td class="col-amount">{formatINR(row.net_fund)}</td>
                        <td class="col-type">
                          <span class="type-pill">{row.type}</span>
                        </td>
                      </tr>
                    {/each}
                  </tbody>
                </table>
              </div>
            {/if}
          {/if}
        {/if}
      </CardContent>
    </Card>
  </div>
</Page>

<style>
  .page-content-wrapper {
    max-width: 1440px;
    margin: 0 auto;
  }

  .ucc-context-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #e0f2fe;
    border: 1px solid #7dd3fc;
    color: #0369a1;
    padding: 10px 16px;
    border-radius: 10px;
    font-size: 13px;
  }

  .clear-ucc-btn {
    background: #ffffff;
    border: 1px solid #0284c7;
    color: #0284c7;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
  }

  .reports-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #ffffff;
    padding: 12px 16px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    flex-wrap: wrap;
  }

  .tabs-segment {
    min-width: 380px;
  }

  .rep-tab {
    font-size: 13px;
    font-weight: 600;
    text-transform: none;
    padding: 6px 12px;
    height: auto;
  }

  .search-input-wrap {
    flex: 1;
    min-width: 200px;
    position: relative;
    display: flex;
    align-items: center;
  }

  .search-input {
    width: 100%;
    height: 34px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    padding: 0 28px 0 12px;
    font-size: 13px;
    color: #0f172a;
    background: #f8fafc;
  }

  .search-input:focus {
    background: #ffffff;
    border-color: #3b82f6;
    outline: none;
  }

  .clear-search {
    position: absolute;
    right: 8px;
    background: none;
    border: none;
    font-size: 12px;
    color: #94a3b8;
    cursor: pointer;
  }

  .actions-group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .table-card {
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    overflow: hidden;
  }

  .data-table-responsive {
    overflow-x: auto;
    width: 100%;
  }

  .rep-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    text-align: left;
  }

  .rep-table thead th {
    background: #f8fafc;
    color: #475569;
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding: 12px 14px;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }

  .rep-table tbody td {
    padding: 12px 14px;
    border-bottom: 1px solid #f1f5f9;
    color: #1e293b;
    vertical-align: middle;
  }

  .rep-table tbody tr:hover td {
    background: #f8fafc;
  }

  .col-index {
    color: #94a3b8;
    font-size: 12px;
  }

  .col-ucc {
    font-family: monospace;
    font-weight: 600;
    color: #2563eb;
  }

  .col-qty {
    font-weight: 600;
  }

  .type-pill {
    background: #f1f5f9;
    color: #475569;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 6px;
    text-transform: uppercase;
  }

  .text-green {
    color: #15803d;
  }

  .text-red {
    color: #b91c1c;
  }

  .loading-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 50px 20px;
    color: #64748b;
  }
</style>
